'use client';

import React from 'react';
import { Button, Card, CardBody } from '@heroui/react';
import { useFormik } from 'formik';
import { createStep2Schema, Step2FormData } from '../validation';
import CustomInput from '../../../../components/input';
import IconInfo from './icon-info.svg';
import IconSucces from './icon-succes.svg';
import AvatarIc from './avatar.jpg';
import { Typography } from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
interface Step2LinkVerificationProps {
  initialValues: Step2FormData;
  onSubmit: (values: Step2FormData) => void;
  onValidationChange: (isValid: boolean) => void;
  isLinkVerified: boolean;
  isCheckingLink: boolean;
  isCheckingCode: boolean;
  isCodeVerified: boolean;
  generatedCode: string;
  onCheckLink: () => void;
  onCheckCode: () => void;
  modelData: any;
}

const Step2LinkVerification: React.FC<Step2LinkVerificationProps> = ({
  initialValues,
  onSubmit,
  onValidationChange,
  isLinkVerified,
  isCheckingLink,
  isCheckingCode,
  isCodeVerified,
  generatedCode,
  onCheckLink,
  onCheckCode,
  modelData,
}) => {
  const { t } = useTranslations();
  const step2Schema = React.useMemo(() => createStep2Schema(t), [t]);
  const formik = useFormik({
    initialValues,
    validationSchema: step2Schema,
    onSubmit,
    enableReinitialize: true,
  });

  React.useEffect(() => {
    const isValid =
      formik.isValid && (isLinkVerified ? isCodeVerified : formik.values.ofLink.trim() !== '');
    onValidationChange(isValid);
    onSubmit(formik.values);
  }, [formik.values, onSubmit]);

  React.useEffect(() => {
    const isValid =
      formik.isValid && (isLinkVerified ? isCodeVerified : formik.values.ofLink.trim() !== '');
    onValidationChange(isValid);
  }, [formik.isValid, formik.values, isLinkVerified, isCodeVerified, onValidationChange]);

  return (
    <>
      <Card className="border border-gray-90 rounded-xl shadow-sm">
        <CardBody className="p-4">
          <form onSubmit={formik.handleSubmit}>
            <div className="space-y-6">
              {/* OnlyFans Link Input */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <label className="text-sm text-gray-900">
                    {t('createAccount.step2.labels.onlyFansLink')}
                    <span className="text-primary">*</span>
                  </label>
                </div>
                <CustomInput
                  name="ofLink"
                  placeholder={t('createAccount.step2.placeholders.linkPlaceholder')}
                  value={formik.values.ofLink}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  variant="bordered"
                  size="sm"
                  className="custom-required-label"
                  isInvalid={formik.touched.ofLink && !!formik.errors.ofLink}
                  errorMessage={formik.touched.ofLink ? formik.errors.ofLink : undefined}
                  classNames={{
                    input: 'text-sm text-auth-ink',
                    inputWrapper: 'border-gray-300 rounded-lg !h-10',
                  }}
                />
                <p className="text-xs text-gray-500 mt-2">{t('createAccount.step2.helper')}</p>
              </div>
            </div>
          </form>
        </CardBody>
      </Card>
      {/* Блок успешной проверки ссылки */}
      {isLinkVerified && (
        <div className="mt-4 space-y-4">
          {/* Зеленый блок успеха */}
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="icon size-8 md:size-9">
                <img src={IconSucces} alt="Success Icon" />
              </div>
              <span className="text-green-800 font-medium">
                {t('createAccount.step2.accountFound')}
              </span>
            </div>
          </div>

          {/* Информация о профиле */}
          <div className="bg-white border border-gray-200 rounded-xl p-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                <img
                  src={modelData.avatar || AvatarIc}
                  alt="Avatar"
                  className="w-full h-full rounded-lg object-cover"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-900 font-medium">
                  <Typography variant="body2">{modelData.name}</Typography>
                </span>
                <a
                  target="__blank"
                  href={`https://onlyfans.com/${modelData.of_user_name}`}
                  className="text-blue-600 hover:underline flex items-center gap-1"
                >
                  <Typography className="!text-partner" variant="body2">
                    @{modelData.of_user_name}
                  </Typography>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Блок с инструкциями и кодом */}
          <Card className="border border-l-[0.1875rem] border-l-partner rounded-none  shadow-sm bg-gray-98">
            <CardBody className="p-4 ">
              <div className="flex items-start gap-4">
                <div className="icon size-8 md:size-9">
                  <img src={IconInfo} alt="" />
                </div>
                <div className="max-w-[46.875rem] flex flex-col items-start">
                  <h3 className="text-sm font-semibold text-partner">
                    {t('createAccount.step2.confirmCode.title')}
                  </h3>
                  <p className="text-sm text-auth-muted leading-5 mb-2">
                    {t('createAccount.step2.confirmCode.instruction')}
                  </p>
                  <p className="text-sm text-partner leading-5 border-[1px] py-0.5 rounded-5 bg-husky px-2 border-husky ">
                    {generatedCode}
                  </p>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
};

export default Step2LinkVerification;
